"use client";

import { useRef } from "react";
import type { ReactNode } from "react";
import { Lock } from "lucide-react";
import {
  DARK_BAND,
  SectionHead,
  SectionShell,
  useBandRowAlign,
  useWholeRowBlocks,
} from "@/components/sections/kit";
import {
  EASE_OUT,
  gsap,
  NOT_REDUCED,
  playOnEnter,
  useGSAP,
} from "@/components/sections/kit/motion";
import { CODA, HOPS } from "./data";

/** Mono syntax palette for the evidence panes (handoff dark-band literals). */
const VAL = "text-[#e8e8f0]";
const CYAN = "text-data-cyan";
const BRAND = "text-brand-400";
const COMMENT = "text-[#565668]";

/** Per-hop evidence artifact — the mono pane that makes each claim checkable.
 *  Bespoke markup, so it lives here rather than in data.ts. Index-aligned
 *  with HOPS. */
const EVIDENCE: ReactNode[] = [
  <>
    GET <span className={CYAN}>https</span>://go.acme.com/launch · certificate:{" "}
    <span className={VAL}>issued automatically</span> · renewal:{" "}
    <span className={VAL}>automatic</span>
  </>,
  <>
    <span className={COMMENT}># public forms only — never the redirect</span>
    <br />
    honeypot: <span className={VAL}>empty</span> <span className={BRAND}>✓</span> · render→submit:{" "}
    <span className={VAL}>3.4 s</span> <span className={BRAND}>✓</span> · turnstile:{" "}
    <span className={VAL}>passed</span> <span className={BRAND}>✓</span>
  </>,
  <>
    GET <span className={VAL}>go.acme.com/launch</span> → <span className={CYAN}>302</span>{" "}
    <span className={VAL}>https://acme.com/launch</span>{" "}
    <span className={COMMENT}>(redis read · config never written here)</span>
  </>,
  <>
    SELECT dest FROM links WHERE slug = $1 <span className={BRAND}>AND tenant_id = $2</span>{" "}
    <span className={COMMENT}>— generated by the repository layer, not optional</span>
  </>,
  // Hop 5's kept/dropped ledger renders its own two-column pane below.
];

/** The hop-5 evidence: the two-column kept/dropped ledger. */
function KeptDroppedLedger() {
  return (
    <div className="mt-4 grid max-w-[640px] rounded-[10px] border border-white/10 bg-white/[0.04] font-jetbrains text-[12px] leading-[1.8] text-[#73738a] sm:grid-cols-2">
      <div className="border-b border-white/[0.08] px-4 py-3 sm:border-b-0 sm:border-r">
        <span className={BRAND}>kept →</span>
        <br />
        <span className={VAL}>
          utc timestamp
          <br />
          country = DE
          <br />
          device = mobile
        </span>
      </div>
      <div className="px-4 py-3">
        <span className={COMMENT}>dropped →</span>
        <br />
        raw ip, after geo lookup
        <br />
        fingerprints — never built
        <br />
        third-party cookies — none
      </div>
    </div>
  );
}

/**
 * The page's signature — the request path. Five hops stack down a vertical
 * spine on the dark band; a gradient line draws down the spine and a glowing
 * request dot travels it, scrubbed to scroll (the request literally moves
 * through the boundary as you read). Each hop rises on entry and slams its
 * dashed seal stamp in with a spring. At rest — SSR, reduced motion, mobile —
 * the spine is fully drawn and every hop, seal and evidence pane is legible.
 */
export default function RequestPathSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  const spineRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);

  // The scrubbed spine: gradient line grows top→bottom while the request dot
  // rides its tip. md+ only (the phone stack keeps the static drawn spine)
  // and motion-safe only; `ease: "none"` on both so scrub stays linear.
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(`${NOT_REDUCED} and (min-width: 768px)`, () => {
        const spine = spineRef.current;
        if (!spine) return;
        // Dot travel: from the line's start (12px inset) to its rest position
        // at the line's end — the spine height minus both insets and the dot.
        const travel = () => -(spine.offsetHeight - 24 - 14);
        const tl = gsap.timeline({
          defaults: { ease: "none" },
          scrollTrigger: {
            trigger: spine,
            start: "top 82%",
            end: "bottom 55%",
            scrub: 0.6,
            invalidateOnRefresh: true,
          },
        });
        tl.from("[data-spine-grad]", { scaleY: 0 }, 0).from("[data-spine-dot]", { y: travel }, 0);
      });
    },
    { scope: spineRef },
  );

  // Each hop rises on its own viewport entry; its seal stamps in 0.25s later
  // (scale 1.8 → rest with a spring settle into the −2.5° resting rotation).
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        const disposers: (() => void)[] = [];
        rootRef.current?.querySelectorAll<HTMLElement>("[data-hop]").forEach((hopEl) => {
          const tl = gsap.timeline({ paused: true, defaults: { immediateRender: false } });
          tl.from(hopEl, { y: 26, autoAlpha: 0, duration: 0.7, ease: EASE_OUT });
          const seal = hopEl.querySelector("[data-seal]");
          if (seal) {
            tl.from(
              seal,
              { scale: 1.8, rotation: -16, autoAlpha: 0, duration: 0.5, ease: "back.out(1.8)" },
              0.25,
            );
          }
          const dispose = playOnEnter(tl, hopEl);
          if (dispose) disposers.push(dispose);
        });
        return () => disposers.forEach((dispose) => dispose());
      });
    },
    { scope: rootRef },
  );

  return (
    <SectionShell
      id="request-path"
      band="dark"
      decoAvoid
      className="scroll-mt-[var(--grid-cell,72px)] max-md:bg-[#14141c] md:pt-px md:pb-px"
    >
      <div ref={rootRef}>
        <SectionHead
          tone="light"
          gridFill={DARK_BAND}
          eyebrow="The request path"
          title="Five hops. Each one sealed."
          lead="Not the data path — the security boundary. See how a click becomes a dashboard row on /analytics; here's what happens to the request itself, hop by hop."
        />

        {/* One grid block: spine, hops and coda clear together on the dark band. */}
        <div data-grid-block data-grid-fill={DARK_BAND}>
          <div ref={spineRef} className="relative">
            {/* The spine: base track, scrub-drawn gradient line, request dot. */}
            <span
              aria-hidden
              className="absolute bottom-3 top-3 left-[31px] w-0.5 bg-white/[0.09] md:left-[59px]"
            />
            <span
              aria-hidden
              data-spine-grad
              className="absolute bottom-3 top-3 left-[31px] w-0.5 origin-top bg-[linear-gradient(180deg,var(--color-brand-400),var(--color-brand-500))] md:left-[59px]"
            />
            <span
              aria-hidden
              data-spine-dot
              className="absolute left-[25px] top-[calc(100%-26px)] hidden h-3.5 w-3.5 rounded-full bg-brand-400 shadow-[0_0_20px_2px_rgba(240,96,144,0.7)] md:left-[53px] md:block"
            />

            <div className="flex flex-col gap-[60px] md:gap-[76px]">
              {HOPS.map((hop, i) => (
                <div
                  key={hop.n}
                  data-hop
                  className="relative grid grid-cols-[64px_minmax(0,1fr)] md:grid-cols-[120px_minmax(0,1fr)]"
                >
                  {/* Node circle — opaque, so it occludes the spine. */}
                  <div className="flex items-start justify-center">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-[#14141c] text-[#e8e8f0]">
                      <hop.icon size={19} strokeWidth={1.8} aria-hidden="true" />
                    </span>
                  </div>

                  <div className="relative min-w-0">
                    <span
                      aria-hidden
                      className="pointer-events-none absolute right-0 select-none font-extrabold tracking-[-0.04em] text-white/[0.045] max-md:-top-3 max-md:text-[52px] md:-top-[22px] md:text-[76px]"
                    >
                      {hop.n}
                    </span>
                    <div className="flex flex-wrap items-center gap-4">
                      <span className="text-[20px] font-bold tracking-[-0.015em] text-white">
                        {hop.name}
                      </span>
                      <span
                        data-seal
                        className="inline-flex -rotate-[2.5deg] items-center gap-[7px] rounded-lg border-[1.5px] border-dashed border-brand-400 px-[11px] py-[5px] font-jetbrains text-[10px] uppercase tracking-[0.09em] text-[#f3d2de]"
                      >
                        <Lock size={10} strokeWidth={2.6} aria-hidden="true" className="text-brand-400" />
                        {hop.seal}
                      </span>
                    </div>
                    <p className="mt-3 max-w-[580px] text-[14px] leading-[1.65] text-[#a6a6b8] [text-wrap:pretty]">
                      {hop.body}
                    </p>
                    {i < EVIDENCE.length ? (
                      <div className="mt-4 max-w-[640px] rounded-[10px] border border-white/10 bg-white/[0.04] px-4 py-3 font-jetbrains text-[12px] leading-[1.7] text-[#73738a] [overflow-wrap:anywhere]">
                        {EVIDENCE[i]}
                      </div>
                    ) : (
                      <KeptDroppedLedger />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-14 font-jetbrains text-[11.5px] text-brand-400 md:ml-[120px]">{CODA}</p>
        </div>
      </div>
    </SectionShell>
  );
}
